import React from 'react';
import { useTheme } from '../../context/ThemeContext';
import { AlertCircle, CheckCircle, Clock, DollarSign, Eye, Target } from 'lucide-react';

export default function ProblemSolution() {
  const { isDark } = useTheme();

  const problems = [
    {
      icon: Eye,
      problem: "0% de visibilité sur l'activité réelle de vos agents",
      solution: "Supervision live de chaque agent avec statuts détaillés"
    },
    {
      icon: Clock,
      problem: "Pauses non contrôlées, temps improductif",
      solution: "Timer intelligent qui exclut automatiquement les pauses"
    },
    {
      icon: Target,
      problem: "KPI flous, objectifs non suivis",
      solution: "Tableaux de bord temps réel avec objectifs personnalisés"
    },
    {
      icon: DollarSign,
      problem: "Commissions calculées manuellement, erreurs fréquentes",
      solution: "Calcul automatique des commissions basé sur les conversions"
    }
  ];

  return (
    <section id="problem" className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className={`text-3xl lg:text-4xl font-bold mb-4 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            Le défi des managers modernes
          </h2>
          <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Sans les bons outils, superviser une équipe commerciale devient un casse-tête
          </p>
        </div>

        {/* Grille problème/solution */}
        <div className="grid md:grid-cols-2 gap-8">
          {problems.map((item, index) => {
            const Icon = item.icon;
            return (
              <div key={index} className={`relative p-8 rounded-2xl border ${
                isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-gray-50 border-gray-200'
              }`}>
                {/* Problème */}
                <div className="flex gap-4 mb-6">
                  <div className="p-3 rounded-xl bg-red-100 dark:bg-red-900/20">
                    <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div>
                    <p className={`text-sm font-medium text-red-600 dark:text-red-400 mb-1`}>
                      Problème
                    </p>
                    <p className={`text-lg font-semibold ${
                      isDark ? 'text-gray-200' : 'text-gray-700'
                    }`}>
                      {item.problem}
                    </p>
                  </div>
                </div>

                {/* Flèche de transition */}
                <div className="flex justify-center my-4">
                  <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                    <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    </svg>
                  </div>
                </div>

                {/* Solution */}
                <div className="flex gap-4">
                  <div className="p-3 rounded-xl bg-emerald-100 dark:bg-emerald-900/20">
                    <CheckCircle className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className={`text-sm font-medium text-emerald-600 dark:text-emerald-400 mb-1`}>
                      Solution Insight360
                    </p>
                    <p className={`text-lg font-semibold ${
                      isDark ? 'text-gray-200' : 'text-gray-700'
                    }`}>
                      {item.solution}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Résultat final */}
        <div className={`mt-16 p-8 rounded-2xl text-center ${
          isDark ? 'bg-gray-900' : 'bg-gray-50'
        }`}>
          <p className={`text-xl font-medium mb-4 ${
            isDark ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Résultat : une équipe 35% plus productive, des managers sereins, des objectifs atteints
          </p>
          <div className="flex justify-center gap-8">
            <div>
              <p className="text-3xl font-bold text-primary-600">+35%</p>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Productivité</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-primary-600">100%</p>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Visibilité</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-primary-600">0</p>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Erreurs de com.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}